'use client';

import { useCallback, useMemo, type DragEvent } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { usePipelineStore } from '@/lib/pipeline/store';
import { NODE_SPECS } from '@/lib/pipeline/registry';
import type { NodeCategory } from '@/lib/pipeline/types';
import PromptNode from './nodes/PromptNode';
import GenerateNode from './nodes/GenerateNode';
import PreviewNode from './nodes/PreviewNode';
import OperatorNode from './nodes/OperatorNode';
import ImageUploadNode from './nodes/ImageUploadNode';
import ImageGalleryNode from './nodes/ImageGalleryNode';
import NumberNode from './nodes/NumberNode';
import TextInputNode from './nodes/TextInputNode';
import SaveToGalleryNode from './nodes/SaveToGalleryNode';
import CropNode from './nodes/CropNode';
import VideoNode from './nodes/VideoNode';

const CATEGORY_COLORS: Record<NodeCategory, string> = {
  Input: '#3b82f6',
  Generator: '#f59e0b',
  Operator: '#14b8a6',
  Draw: '#ec4899',
  Output: '#a855f7',
};

export default function PipelineCanvas() {
  const nodes = usePipelineStore((s) => s.nodes);
  const edges = usePipelineStore((s) => s.edges);
  const onNodesChange = usePipelineStore((s) => s.onNodesChange);
  const onEdgesChange = usePipelineStore((s) => s.onEdgesChange);
  const onConnect = usePipelineStore((s) => s.onConnect);
  const addNode = usePipelineStore((s) => s.addNode);
  const pruneNodeState = usePipelineStore((s) => s.pruneNodeState);
  const { screenToFlowPosition } = useReactFlow();

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData('application/reactflow');
      if (!type) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      addNode(type, position);
    },
    [screenToFlowPosition, addNode],
  );

  const onNodesDelete = useCallback(
    (deleted: Node[]) => pruneNodeState(deleted.map((n) => n.id)),
    [pruneNodeState],
  );

  const nodeTypes = useMemo(
    () => ({
      promptInput: PromptNode,
      generate: GenerateNode,
      preview: PreviewNode,
      imageUpload: ImageUploadNode,
      imageGallery: ImageGalleryNode,
      textInput: TextInputNode,
      numberInput: NumberNode,
      removeBackground: OperatorNode,
      upscale: OperatorNode,
      faceRestore: OperatorNode,
      depth: OperatorNode,
      resize: OperatorNode,
      crop: CropNode,
      autoTrim: OperatorNode,
      padFrame: OperatorNode,
      adjustColors: OperatorNode,
      blur: OperatorNode,
      seamless: OperatorNode,
      solidColor: OperatorNode,
      gradient: OperatorNode,
      grid: OperatorNode,
      noise: OperatorNode,
      text: OperatorNode,
      shapes: OperatorNode,
      outline: OperatorNode,
      composite: OperatorNode,
      applyMask: OperatorNode,
      detect: OperatorNode,
      segment: OperatorNode,
      caption: OperatorNode,
      textJoin: OperatorNode,
      pick: OperatorNode,
      animateVideo: VideoNode,
      saveToGallery: SaveToGalleryNode,
    }),
    [],
  );

  const minimapColor = useCallback(
    (n: Node) => CATEGORY_COLORS[NODE_SPECS[n.type as string]?.category] ?? '#52525b',
    [],
  );

  // Only allow connecting sockets of matching type.
  const isValidConnection = useCallback((c: Connection | Edge) => {
    const conn = c as Connection;
    const ns = usePipelineStore.getState().nodes;
    const src = ns.find((n) => n.id === conn.source);
    const tgt = ns.find((n) => n.id === conn.target);
    if (!src || !tgt) return false;
    const out = NODE_SPECS[src.type as string]?.outputs.find((h) => h.id === conn.sourceHandle);
    const inp = NODE_SPECS[tgt.type as string]?.inputs.find((h) => h.id === conn.targetHandle);
    if (!out || !inp) return false;
    if (out.type === inp.type) return true;
    // A mask is a displayable/usable image: allow MASK → IMAGE.
    if (out.type === 'MASK' && inp.type === 'IMAGE') return true;
    // ANY (Pick/reroute) connects to/from anything.
    if (out.type === 'ANY' || inp.type === 'ANY') return true;
    return false;
  }, []);

  return (
    <div className="relative w-full h-full" onDrop={onDrop} onDragOver={onDragOver}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        nodeTypes={nodeTypes}
        isValidConnection={isValidConnection}
        deleteKeyCode={['Backspace', 'Delete']}
        defaultEdgeOptions={{ animated: true }}
        proOptions={{ hideAttribution: true }}
        fitView
        colorMode="dark"
      >
        <Background color="#27272a" gap={18} />
        <Controls />
        {nodes.length > 0 && <MiniMap pannable zoomable nodeColor={minimapColor} />}
      </ReactFlow>

      {nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <div className="text-4xl mb-3 opacity-60">🧩</div>
            <h2 className="text-base font-semibold text-[var(--text-secondary)]">
              Build a pipeline
            </h2>
            <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed">
              Drag a node from the left — or click it — to drop it on the canvas.
              Wire each <span className="text-[var(--text-secondary)]">output → input</span>,
              then press <span className="text-[var(--text-secondary)]">Run</span>.
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-3">
              Or open <span className="text-[var(--text-secondary)]">Library</span> for a
              ready-made template.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
